import { Location } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { PageHeaderComponent } from '../../../shared/page-header/page-header';
import { SummaryCardComponent } from '../../../shared/summary-card/summary-card';
import {
  PaginationRequest,
  TableActionEvent,
  TableColumn,
  TablePageEvent,
  TableSortEvent,
} from '../../../shared/data-table-paginado/data-table-paginado.models';
import { DataTablePaginado } from '../../../shared/data-table-paginado/data-table-paginado';
import { EmpresaService } from '../../../core/services/empresa.service';
import { finalize } from 'rxjs';
import {
  EmpresaFormValue,
  EmpresasDataModel,
  EmpresasInfoModel,
} from '../../../core/models/empresa.data.model';
import { FormsModule } from '@angular/forms';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell';
import { EmpresasFormComponent } from './empresas-form/empresas-form';
import { UiButtonComponent } from '../../../shared/ui-button/ui-button';
import { Plan } from '../../../core/models/plan.model';
import { PlanService } from '../../../core/services/plan.service';
import { ApiService } from '../../../core/services/api.service';
import {
  ConfirmationModalComponent,
  ConfirmationStatus,
} from '../../../shared/confirmation-modal/confirmation-modal';
import { SearchInput } from '../../../shared/search-input/search-input';
import { CardListComponent } from '../../../shared/card-list/card-list';
import { SelectOption, SelectComponent } from '../../../shared/select/select';
import { MobileService } from '../../../core/services/mobile.service';
import { LoadingComponent } from '../../../shared/loading/loading';
import dayjs from 'dayjs';

@Component({
  selector: 'app-empresas',
  standalone: true,
  imports: [
    PageHeaderComponent,
    SummaryCardComponent,
    DataTablePaginado,
    FormsModule,
    ModalShellComponent,
    EmpresasFormComponent,
    UiButtonComponent,
    ConfirmationModalComponent,
    SearchInput,
    SelectComponent,
    CardListComponent,
    LoadingComponent,
  ],
  templateUrl: './empresas.html',
  styleUrl: './empresas.css',
})
export class EmpresasComponent {
  private readonly location = inject(Location);
  private readonly empresaService = inject(EmpresaService);
  private readonly planService = inject(PlanService);
  private readonly apiService = inject(ApiService);
  protected readonly mobileService = inject(MobileService);

  //Tarjetas
  protected infoEmpresas: EmpresasInfoModel | null = null;
  protected loadingCard = false;
  protected loadingPage = true;
  private initialLoadsPending = 3;

  //tabla
  protected data: EmpresasDataModel[] = [];
  protected loadingTable = false;
  protected page = 1;
  protected pageSize = 10;
  protected totalItems = 0;
  protected searchInput = '';
  protected search = '';
  protected statusFilter = 'Activo';
  protected readonly statusOptions: SelectOption[] = [
    { label: 'Todos', value: 'Todos' },
    { label: 'Activas', value: 'Activo' },
    { label: 'Inactivas', value: 'Inactivo' },
  ];
  protected sortBy: string | undefined;
  protected sortDirection: 'asc' | 'desc' | undefined;
  protected readonly pageSizeOptions = [10, 25, 50, 100];

  protected formatDate(value: string | null | undefined): string {
    if (!value) return '-';

    const date = dayjs(value);
    return date.isValid() ? date.format('YYYY-MM-DD') : '-';
  }

  protected empresaEditar: EmpresaFormValue | null = null;
  protected planes: Plan[] = [];
  protected showForm = false;
  protected readonly saveError = signal('');

  protected colorIcon = 'var(--red)';
  protected backgroundIcon = 'rgba(255, 0, 0, 0.1)';

  protected empresaToDelete: EmpresasDataModel | null = null;
  protected deleteStatus: ConfirmationStatus = 'active';
  protected deleteMessage = '';
  protected showDeleteConfirmation = false;
  protected deletingEmpresa = false;

  //columnas
  protected columns: TableColumn<EmpresasDataModel>[] = [
    {
      key: 'empresa',
      label: 'Empresa',
      sortable: true,
    },
    {
      key: 'plan',
      label: 'Plan',
      sortable: true,
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      type: 'badge',
      align: 'center',
    },
    {
      key: 'usuarios',
      label: 'Usuarios',
      type: 'number',
      sortable: true,
      align: 'center',
    },
    {
      key: 'fechaInicio',
      label: 'Fecha de inicio',
      type: 'date',
      sortable: true,
    },
    {
      key: 'fechaFin',
      label: 'Fecha de fin',
      type: 'date',
      sortable: true,
    },
    {
      key: '__actions',
      label: 'Acciones',
      type: 'actions',
      align: 'center',
      width: '120px',
      actions: [
        {
          id: 'edit',
          label: 'Editar',
          icon: 'fa-solid fa-pen',
          ariaLabel: 'Editar empresa',
        },
        {
          id: 'delete',
          label: 'Eliminar',
          icon: 'fa-solid fa-trash',
          ariaLabel: 'Eliminar empresa',
          cssClass: 'delete-action',
        },
      ],
    },
  ];

  //navegacion
  protected volver(): void {
    this.location.back();
  }

  constructor() {
    this.loadCard();
    this.loadEmpresas();
    this.loadPlanes();
  }

  //tarjetas
  protected loadCard(): void {
    this.loadingCard = true;

    this.empresaService
      .getEmpresasInfo()
      .pipe(
        finalize(() => {
          this.loadingCard = false;
          this.finishInitialLoad();
        }),
      )
      .subscribe({
        next: (result) => {
          this.infoEmpresas = result ?? null;
        },

        error: () => {
          this.infoEmpresas = null;
        },
      });
  }

  //tabla
  protected loadEmpresas(): void {
    this.loadingTable = true;

    const request: PaginationRequest = {
      page: this.page,
      pageSize: this.pageSize,
      search: this.search,
      status: this.statusFilter,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
    };

    this.empresaService
      .getEmpresasDataPaginada(request)
      .pipe(
        finalize(() => {
          this.loadingTable = false;
          this.finishInitialLoad();
        }),
      )
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            this.data = [];
            this.totalItems = 0;

            return;
          }

          const result = response.data;

          this.data = result.items;

          this.page = result.page;

          this.totalItems = result.totalItems;
        },

        error: () => {
          this.data = [];

          this.totalItems = 0;
        },
      });
  }

  //paginacion
  protected onPageChange(event: TablePageEvent): void {
    this.page = event.page;

    this.pageSize = event.pageSize;

    this.loadEmpresas();
  }

  protected onPageSizeChange(event: TablePageEvent): void {
    this.page = 1;

    this.pageSize = event.pageSize;

    this.loadEmpresas();
  }

  //ordenamiento
  protected onSort(event: TableSortEvent): void {
    this.page = 1;

    this.sortBy = event.column ?? undefined;

    this.sortDirection = event.direction ?? undefined;

    this.loadEmpresas();
  }

  //busqueda
  protected onSearch(): void {
    this.search = this.searchInput.trim();

    this.page = 1;

    this.loadEmpresas();
  }

  protected onStatusChange(value: string | string[] | null): void {
    if (typeof value !== 'string') return;
    this.statusFilter = value;
    this.page = 1;
    this.loadEmpresas();
  }

  //accion
  protected onAction(event: TableActionEvent<EmpresasDataModel>): void {
    switch (event.action) {
      case 'edit':
        this.editarEmpresa(event.row);
        break;

      case 'delete':
        this.eliminarEmpresa(event.row);
        break;
    }
  }

  protected getBadgeClass(status: 'ACTIVA' | 'INACTIVA'): string {
    return status === 'ACTIVA' ? 'badge-active' : 'badge-inactive';
  }

  protected eliminarEmpresa(empresa: EmpresasDataModel): void {
    if (this.deletingEmpresa) return;

    this.empresaToDelete = empresa;
    this.deleteStatus = 'active';
    this.deleteMessage = `Se eliminará la empresa ${empresa.empresa}. Esta acción no se puede deshacer.`;
    this.showDeleteConfirmation = true;
  }

  protected confirmarEliminarEmpresa(): void {
    if (!this.empresaToDelete || this.deletingEmpresa) return;

    this.deletingEmpresa = true;

    this.empresaService
      .desactivate(this.empresaToDelete.empresaId)
      .pipe(finalize(() => (this.deletingEmpresa = false)))
      .subscribe({
        next: (response) => {
          if (!response.data) {
            this.deleteStatus = 'error';
            this.deleteMessage = 'La empresa no pudo ser eliminada.';
            return;
          }

          this.deleteStatus = 'success';
          this.colorIcon = 'var(--green)';
          this.backgroundIcon = 'rgba(0, 128, 0, 0.1)';
          this.deleteMessage = 'La empresa fue eliminada correctamente.';

          this.loadCard();
          this.loadEmpresas();
        },
        error: (error: HttpErrorResponse) => {
          this.deleteStatus = 'error';
          this.deleteMessage = this.apiService.getMessage(
            error,
            'No se pudo eliminar la empresa. Intenta nuevamente.',
          );
        },
      });
  }

  protected cancelarEliminarEmpresa(): void {
    this.closeDeleteConfirmation();
  }

  protected closeDeleteConfirmation(): void {
    if (this.deletingEmpresa) return;

    this.showDeleteConfirmation = false;
    this.empresaToDelete = null;
    this.deleteStatus = 'active';
    this.deleteMessage = '';
    this.colorIcon = 'var(--red)';
    this.backgroundIcon = 'rgba(255, 0, 0, 0.1)';
  }

  protected nuevaEmpresa(): void {
    this.empresaEditar = null;
    this.saveError.set('');
    this.openForm();
  }

  protected editarEmpresa(empresa: EmpresasDataModel): void {
    this.saveError.set('');
    this.empresaEditar = {
      idEmpresa: empresa.empresaId,
      razonSocial: empresa.empresa,
      identificacionFiscal: empresa.identificacionFiscal ?? '',
      planId: empresa.planId ?? this.planes.find((plan) => plan.name === empresa.plan)?.id ?? '',
      fechaInicio: empresa.fechaInicio,
      fechaFin: empresa.fechaFin,
      estado: empresa.estado,
    };
    this.openForm();
  }

  protected onEmpresaSaved(value: EmpresaFormValue): void {
    const { idEmpresa, ...request } = value;

    const operation = idEmpresa
      ? this.empresaService.update(idEmpresa, request)
      : this.empresaService.create(request);

    operation.subscribe({
      next: (response) => {
        if (!response.success || !response.data) {
          this.saveError.set(
            response.message ?? 'No se pudo guardar la empresa. Intenta nuevamente.',
          );
          return;
        }

        this.closeForm();
        this.loadCard();
        this.loadEmpresas();
      },
      error: (error: HttpErrorResponse) => {
        this.saveError.set(
          this.apiService.getMessage(error, 'No se pudo guardar la empresa. Intenta nuevamente.'),
        );
      },
    });
  }

  protected openForm(): void {
    this.showForm = true;
  }

  protected closeForm(): void {
    this.empresaEditar = null;
    this.saveError.set('');
    this.showForm = false;
  }

  private loadPlanes(): void {
    this.planService
      .getAll()
      .pipe(finalize(() => this.finishInitialLoad()))
      .subscribe({
        next: (planes) => (this.planes = planes),
        error: () => (this.planes = []),
      });
  }

  private finishInitialLoad(): void {
    if (this.initialLoadsPending === 0) return;
    this.initialLoadsPending -= 1;
    if (this.initialLoadsPending === 0) this.loadingPage = false;
  }
}
